import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";

export default function ParticipantsPanel({ operationId, canManage, entreprises = [] }) {
  const { roles, roleLabels } = useApp();
  const [parts, setParts] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ userId: "", role: "OBSERVATEUR", entrepriseId: "" });
  const scoped = form.role === "ENTREPRISE_EXTERIEURE" || form.role === "SOUS_TRAITANT";

  const load = useCallback(() => {
    api.get(`/operations/${operationId}/participants`).then((r) => setParts(r.data)).catch(() => {});
    if (canManage) api.get(`/users`).then((r) => setUsers(r.data)).catch(() => {});
  }, [operationId, canManage]);
  useEffect(load, [load]);

  const add = async () => {
    if (!form.userId) { toast.error("Sélectionnez un utilisateur"); return; }
    try {
      await api.post(`/operations/${operationId}/participants`, {
        userId: form.userId, role: form.role, entrepriseId: scoped ? form.entrepriseId || null : null,
      });
      toast.success("Participant ajouté");
      setForm({ userId: "", role: "OBSERVATEUR", entrepriseId: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (pid) => {
    try { await api.delete(`/operations/${operationId}/participants/${pid}`); toast.success("Participant retiré"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const memberIds = new Set(parts.map((p) => p.userId));
  const available = users.filter((u) => !memberIds.has(u.id));

  return (
    <div className="card" data-testid="participants-panel">
      <h3 style={{ fontSize: "1rem", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
        <ShieldCheck size={16} /> Équipe de l'opération <span className="tag">{parts.length}</span>
      </h3>
      <p style={{ fontSize: ".82rem", color: "var(--text-muted)", marginBottom: 12 }}>
        Rôles et droits contrôlés côté serveur. Les entreprises extérieures et sous-traitants n'agissent que sur leur propre périmètre.
      </p>

      <div className="tablewrap"><table>
        <thead><tr><th>Utilisateur</th><th>Rôle</th><th>Entreprise</th><th>Statut</th><th></th></tr></thead>
        <tbody>
          {parts.map((p) => (
            <tr key={p.id} data-testid={`participant-${p.userId}`}>
              <td><b>{p.utilisateur?.name || p.userId}</b><div style={{ fontSize: ".72rem", color: "var(--text-muted)" }}>{p.utilisateur?.email}</div></td>
              <td><span className="tag">{p.roleLabel || roleLabels[p.role] || p.role}</span></td>
              <td className="mono" style={{ fontSize: ".78rem" }}>{p.entrepriseId || "—"}</td>
              <td style={{ fontSize: ".78rem" }}>{p.proprietaire ? "Propriétaire" : (p.statutInvitation === "invite" ? "Invité" : "Actif")}</td>
              <td>{canManage && !p.proprietaire && (
                <button className="btn-mini" onClick={() => remove(p.id)} data-testid={`participant-remove-${p.userId}`}><Trash2 size={12} /></button>
              )}</td>
            </tr>
          ))}
        </tbody>
      </table></div>

      {canManage && (
        <div style={{ marginTop: 16, borderTop: "1px solid var(--ligne)", paddingTop: 14 }} data-testid="participant-add-form">
          <label className="f">Ajouter un participant</label>
          <div className="row3">
            <select data-testid="participant-user" value={form.userId} onChange={(e) => setForm((f) => ({ ...f, userId: e.target.value }))}>
              <option value="">— Utilisateur —</option>
              {available.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
            <select data-testid="participant-role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              {roles.map((r) => <option key={r} value={r}>{roleLabels[r] || r}</option>)}
            </select>
            {scoped ? (
              <select data-testid="participant-entreprise" value={form.entrepriseId} onChange={(e) => setForm((f) => ({ ...f, entrepriseId: e.target.value }))}>
                <option value="">— Entreprise —</option>
                {entreprises.map((e) => <option key={e.id} value={e.id}>{e.nom}</option>)}
              </select>
            ) : <div />}
          </div>
          <button className="btn btn-p btn-sm" style={{ marginTop: 10 }} onClick={add} data-testid="participant-add-btn"><UserPlus size={14} />Ajouter</button>
        </div>
      )}
    </div>
  );
}
